<template>
  <main class="magenta-black-days">
    <div class="magenta-black-days__breadcrumb">
      <SingleLevelBreadcrumb label="Magenta Black Days" />
    </div>
    <GridContainer class="magenta-black-days__stage">
      <GridRow>
        <GridColumn>
          <h1 class="magenta-black-days__headline">
            <span class="magenta-black-days__headline--top">Magenta</span>
            <span class="magenta-black-days__headline--bottom">
              <span class="magenta-black-days__headline--white">Black </span>
              Days
            </span>
          </h1>
          <Heading
            class="magenta-black-days__subline"
            font-weight="bold"
            size="md"
            tag="h2"
            align="center"
          >
            Die besten Aktionsangebote des Jahres - nur bis zum 04.12.
          </Heading>
          <CdnImage
            render-without-figure
            class="magenta-black-days__shooting-star"
            src="/content/content-images/magenta-black-days/lp/04b5a7bc010b61645adc43025e1fc558/sternschnuppe.svg"
            alt=""
            lazy
          />
        </GridColumn>
      </GridRow>
    </GridContainer>
    <GridContainer class="magenta-black-days__products">
      <GridRow>
        <GridColumn>
          <OfferPromotionTilesContainer
            v-if="firstRowOffers && firstRowOffers.length > 0"
            :promotion-offers="firstRowOffers"
            class="magenta-black-days__row magenta-black-days__two-columns"
          />
        </GridColumn>
      </GridRow>
      <GridRow>
        <GridColumn>
          <OfferPromotionTilesContainer
            v-if="secondRowOffers && secondRowOffers.length > 0"
            :promotion-offers="secondRowOffers"
            class="magenta-black-days__row magenta-black-days__one-column"
          />
        </GridColumn>
      </GridRow>
      <GridRow>
        <GridColumn>
          <OfferPromotionTilesContainer
            v-if="thirdRowOffers && thirdRowOffers.length > 0"
            :promotion-offers="thirdRowOffers"
            always-lazy
            class="magenta-black-days__row magenta-black-days__two-columns"
          />
        </GridColumn>
      </GridRow>
      <GridRow>
        <GridColumn>
          <OfferTilesContainer
            v-if="otherOffers && otherOffers.length > 0"
            :offer-tile-container-props="OFFER_TILE_CONTAINER_PROPS"
            :offers="otherOffers"
            always-lazy
            tag-title="h2"
            class="magenta-black-days__row magenta-black-days__three-columns"
          >
            <template #beforeOfferTiles>
              <div class="magenta-black-days__before-tiles">
                <OfferPromotionTilesContainer
                  v-if="fourthRowOffers && fourthRowOffers.length > 0"
                  :promotion-offers="fourthRowOffers"
                  always-lazy
                  class="magenta-black-days__row magenta-black-days__one-column"
                />
              </div>
            </template>
            <template #afterOfferTiles>
              <div class="magenta-black-days__after-tiles">
                <Card v-bind="CARD_PROPS">
                  <template #image>
                    <CdnImage
                      src="/content/content-images/magenta-black-days/lp/7fc24af953b31a335b1d5c95d8fb6ccf/apple-cases-1000x1000@2x.webp"
                      alt="iPhone Cases"
                      sizes="xs:80vw sm:50vw md:760px"
                      lazy
                    />
                  </template>
                  <template #label>
                    <span class="magenta-black-days__brand-logo">
                      <CdnImage
                        v-if="APPLE_BRAND?.logo"
                        :src="APPLE_BRAND.logo"
                        :alt="`${APPLE_BRAND.name} Logo`"
                        :image-style="`aspect-ratio: ${APPLE_BRAND.aspectRatio}`"
                        :width="25"
                        :height="30"
                        render-without-figure
                        lazy
                      />
                    </span>
                  </template>
                  <template #action>
                    <LinkButton
                      tag="span"
                      secondary
                    >
                      Zum Angebot
                    </LinkButton>
                  </template>
                </Card>
              </div>
            </template>
          </OfferTilesContainer>
        </GridColumn>
      </GridRow>
    </GridContainer>
  </main>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useLazyAsyncData, useHead } from '#imports';
import { getHeader } from '~/helpers/page-header-compositon';
import { Track } from '~/helpers/page-tracking';

import SingleLevelBreadcrumb from '~/components/single-level-breadcrumb.vue';
import CdnImage from '~/components/cdn-image.vue';
import OfferTilesContainer from '~/components/offer-tiles-container.vue';
import OfferPromotionTilesContainer from '~/components/offer-promotion-tiles-container.vue';
import {
  Heading,
  GridContainer,
  GridRow,
  GridColumn,
  Card,
  LinkButton,
} from '@i22/td-longtail-vue-components';
import {
  loadOffersForPromotions,
  getOffersByGroup,
  type Promotion,
  type PromotionOffer,
} from '~/lib/offers-by-group';
import { addLogo } from '~/lib/brand';
import { type OfferTileContainerProps } from '~~/components/offer-tile-container.vue';
import type { CardComponentProps } from '@i22/td-longtail-vue-components/dist/components/card/card.vue';

const OFFER_TILE_CONTAINER_PROPS: Partial<OfferTileContainerProps> = {
  hideDeliveryTime: false,
  hideVariants: true,
  hideContract: true,
  hideEnergyLabel: true,
  hideBookmark: true,
  hideAppIcons: true,
  showGoToOfferButton: true,
};

const OFFER_TILE_CONTAINER_HIGHLIGHT_PROPS: Partial<OfferTileContainerProps> = {
  ...OFFER_TILE_CONTAINER_PROPS,
  card: {
    imagePosition: 'right',
    customClass: 'card--highlight',
  },
};

const CARD_PROPS: CardComponentProps = {
  imagePosition: 'right',
  customClass: 'card--promo bg-primary',
  tag: 'nuxt-link',
  href: '/zubehoer/taschen-huellen/_apple?productProviders=TELEKOM&notFilters=ipad,airtag',
  heading: {
    tag: 'h2',
    text: '30% Rabatt auf viele iPhone Cases!',
  },
  squareBadgeReverse: true,
};

const APPLE_BRAND = addLogo({
  name: 'Apple',
  slug: 'apple',
});

const PRODUCT_DATA: Promotion[] = [
  {
    articleNumber: '0711719021872', // PlayStation 5 Digital-Edition 825 GB Bundle
    tileGroups: ['first'],
    offerTileContainerProps: {
      ...OFFER_TILE_CONTAINER_HIGHLIGHT_PROPS,
      card: {
        ...OFFER_TILE_CONTAINER_HIGHLIGHT_PROPS.card,
        customStyles: "--data-content: 'Verfügbar ab 21.11.'",
        squareBadgeReverse: true,
      },
    },
  },
  {
    articleNumber: '99936037', // Apple - AirPods Max Polarstern
    tileGroups: ['first'],
    offerTileContainerProps: {
      ...OFFER_TILE_CONTAINER_HIGHLIGHT_PROPS,
      card: {
        ...OFFER_TILE_CONTAINER_HIGHLIGHT_PROPS.card,
        customClass: 'card--highlight bg-primary',
        squareBadgeReverse: true,
      },
    },
  },
  {
    articleNumber: '8806097253044', // Samsung - 55" Crystal UHD U7079F 4K Smart TV (2025)
    tileGroups: ['second'],
    offerTileContainerProps: {
      ...OFFER_TILE_CONTAINER_HIGHLIGHT_PROPS,
      imageIndex: 1,
      card: {
        ...OFFER_TILE_CONTAINER_HIGHLIGHT_PROPS.card,
        squareBadgeReverse: true,
      },
    },
  },
  {
    articleNumber: '0194644040017', // Anker Soundcore - Soundcore Boom 2 SE
    tileGroups: ['third'],
    offerTileContainerProps: {
      ...OFFER_TILE_CONTAINER_HIGHLIGHT_PROPS,
      card: {
        ...OFFER_TILE_CONTAINER_HIGHLIGHT_PROPS.card,
        customClass: 'card--highlight bg-primary',
        squareBadgeReverse: true,
      },
    },
  },
  {
    articleNumber: '99937192', // Apple - AirPods Pro 3
    tileGroups: ['third'],
    offerTileContainerProps: {
      ...OFFER_TILE_CONTAINER_HIGHLIGHT_PROPS,
      card: {
        ...OFFER_TILE_CONTAINER_HIGHLIGHT_PROPS.card,
        squareBadgeReverse: true,
      },
    },
  },
  {
    articleNumber: '4250494923484', // Homematic IP - Heizkörperthermostat 3er Set
    tileGroups: ['fourth'],
    offerTileContainerProps: {
      ...OFFER_TILE_CONTAINER_HIGHLIGHT_PROPS,
      card: {
        ...OFFER_TILE_CONTAINER_HIGHLIGHT_PROPS.card,
        customClass: 'card--highlight bg-primary',
        squareBadgeReverse: true,
      },
    },
  },
  {
    articleNumber: '99934801', // Apple - AirPods Pro (2.Gen.) MagSafe USB-C
  },
  {
    articleNumber: '99936633', // Anker - PowerBank 25000mAh mit USB-C Kabel
  },
  {
    articleNumber: '0194644290528', // Anker Soundcore - Soundcore Rave 3S
  },
  {
    articleNumber: '6932554447052', // Xiaomi - Xiaomi S40 Pro Staubsaugerroboter
  },
  {
    articleNumber: '8718819375883', // Fairphone - Fairbuds
  },
  {
    articleNumber: '0840390335012', // Zagg - Mophie AirPods Max Ladestation mit magnetischem USB-C & kabellosem Ladepad
  },
  {
    articleNumber: '4049702653950', // Homematic IP - Heizkörperthermostat - basic 3er Set
  },
  {
    articleNumber: '4029164140250', // Homematic IP - Bundle Schalt-Mess-Kabel außen + Access Point
  },
  {
    articleNumber: '4029164136017', // Homematic IP - Starter Set Heizen Basic für 3 Heizkörper
  },
  {
    articleNumber: '8806097253167', // Samsung - GU65U7079FUXZG - 65" Crystal UHD U7079F 4K Smart TV (2025)
  },
  {
    articleNumber: '8806097252504', // Samsung - GU50U7079FUXZG
  },
  {
    articleNumber: '8806095455174', // Samsung - 65 Zoll Neo QLED 4K QN95D Graphit Schwarz
  },
  {
    articleNumber: '4048945035110', // Teufel - Boomster 3
  },
  {
    articleNumber: '4048945030085', // Teufel - Rockster Cross
  },
  {
    articleNumber: '4068568996841', // Eve - EVE Thermo (Matter) 3er Pack
  },
  {
    articleNumber: '4068568918331', // Eve - Eve Energy EU (Matter) 3er Pack
  },
  {
    articleNumber: '8720618634344', // Zens - MagSafe 4-in-1 Charging station
  },
  {
    articleNumber: '6953281008941', // imoo - Z1
  },
];

Track({ page_type: 'theme.landing' });
getHeader({
  isIndexed: ref(false),
  title: ref('Magenta Black Days'),
  description: ref(
    'Die besten Aktionsangebote des Jahres nur für kurze Zeit und nur solange der Vorrat reicht. Jetzt im Telekom Zubehörshop entdecken!'
  ),
});

useHead({
  link: [
    {
      rel: 'preload',
      as: 'image',
      href: '/content/content-images/magenta-black-days/lp/889479e2ae2ac319f8f161d0c491c3d9/kachel-himmel-bg@2x.png',
    },
  ],
});

const { data: offers, error: errorData } = useLazyAsyncData(
  'magenta-black-days',
  async (): Promise<PromotionOffer[]> => loadOffersForPromotions(PRODUCT_DATA, false, false)
);

const firstRowOffers = computed(() => getOffersByGroup(offers.value, 'first'));

const secondRowOffers = computed(() => getOffersByGroup(offers.value, 'second'));

const thirdRowOffers = computed(() => getOffersByGroup(offers.value, 'third'));

const fourthRowOffers = computed(() => getOffersByGroup(offers.value, 'fourth'));

const otherOffers = computed(() => {
  if (!offers.value) return [];
  return offers.value
    .filter((offer) => !offer.tileGroups || offer.tileGroups.length === 0)
    .map((p) => p.offer)
    .filter(Boolean);
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.magenta-black-days {
  background: black
    url('/content/content-images/magenta-black-days/lp/889479e2ae2ac319f8f161d0c491c3d9/kachel-himmel-bg@2x.png');
  background-size: 768px;
  background-position: center;
  color: white;
  overflow-x: clip;

  &__breadcrumb {
    background-color: white;
  }

  &__stage {
    margin-top: 60px;

    @include mq($mq-md) {
      margin-top: 120px;
    }
  }

  &__shooting-star {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 0;
    max-width: 150px;

    @include mq($mq-md) {
      max-width: 250px;
    }
    @include mq($mq-lg) {
      top: 17%;
      max-width: 280px;
    }
  }

  &__headline {
    position: relative;
    z-index: 1;
    color: #e20074;
    font-feature-settings:
      'liga' off,
      'clig' off;
    font-family: TeleNeo;
    font-weight: 900;
    text-transform: uppercase;
    text-align: center;
    line-height: 44px;
    margin-bottom: 60px;

    @include mq($mq-md) {
      line-height: 72px;
    }

    @include mq($mq-lg) {
      line-height: 116px;
    }

    &--white {
      color: white;
    }

    &--top {
      display: block;
      font-size: 58px;

      @include mq($mq-md) {
        font-size: 97px;
      }

      @include mq($mq-lg) {
        font-size: 160px;
      }
    }

    &--bottom {
      display: block;
      font-size: 59.5px;
      margin-left: -4px;

      @include mq($mq-md) {
        font-size: 100px;
        margin-left: -7px;
      }

      @include mq($mq-lg) {
        font-size: 165px;
        margin-left: -10px;
      }
    }
  }

  &__footer {
    margin-bottom: 120px;
  }

  &__products {
    margin-top: 1rem;
    margin-bottom: 260px;
  }

  &__row {
    margin-bottom: 0px;
    @include mq($mq-sm) {
      margin-bottom: 60px;
    }

    &.magenta-black-days__one-column {
      --offer-grid-columns-xs: 1;
      --offer-grid-columns-sm: 1;
      --offer-grid-columns-md: 1;
      --offer-grid-columns-lg: 1;
    }

    &.magenta-black-days__two-columns {
      --offer-grid-columns-xs: 1;
      --offer-grid-columns-sm: 1;
      --offer-grid-columns-md: 2;
      --offer-grid-columns-lg: 2;
    }

    &.magenta-black-days__three-columns {
      --offer-grid-columns-xs: 1;
      --offer-grid-columns-sm: 2;
      --offer-grid-columns-md: 3;
      --offer-grid-columns-lg: 3;
    }
  }

  &__before-tiles {
    grid-row: 2;
    grid-column: 1 / -1;
    margin-top: 0;
    @include mq($mq-sm) {
      margin-top: 60px;
    }

    .magenta-black-days__row {
      padding: 0;
    }
  }

  &__after-tiles {
    grid-row: 12;
    @include mq($mq-md) {
      grid-row: 7;
    }
    grid-column: 1 / -1;

    @include mq($mq-sm) {
      margin-top: 60px;
      margin-bottom: 60px;
    }

    .magenta-black-days__row {
      padding: 0;
    }
  }

  .magenta-black-days__one-column {
    :deep(.card) {
      min-height: 400px;
      @include mq($mq-sm) {
        max-height: 400px;
      }
    }
  }

  :deep(.card) {
    --card-padding: 2rem;
    --card-bg-color: var(--color-grey-900);
    --card-image-container-width: calc(50% - 20px);
    --focus-outline: 2px solid var(--color-primary);
    --price-strikethrough-color: currentColor;

    .card__background {
      background-image: radial-gradient(
        circle at top center,
        rgba(255, 255, 255, 0.15) 0%,
        rgba(255, 255, 255, 0) 30%
      );
    }

    .card__action {
      grid-gap: 0;
    }

    .offer-tile__button {
      justify-content: start;
    }

    .filter-invert {
      filter: invert(100%);
    }

    &.card--highlight {
      --card-radius: 3rem;
      --color-error: var(--color-secondary);
      min-height: 400px;
      position: relative;

      @include mq($mq-sm) {
        --card-padding: 6rem 3.5rem 3.5rem 3.5rem;
      }

      @include mq($mq-md) {
        --card-padding: 8rem 3.5rem 3.5rem 3.5rem;
      }

      .card__background {
        border: 4px solid var(--color-primary);
        border-radius: 3rem;
        background-image: radial-gradient(
          circle at top right,
          rgba(255, 255, 255, 0.15) 0%,
          rgba(255, 255, 255, 0) 30%
        );

        &::before {
          content: var(--data-content, 'Top-Angebot');
          position: absolute;
          z-index: 0;
          top: 0;
          left: 0;
          background-color: var(--color-primary);
          color: white;
          padding: 12px 26px;
          font-size: 12px;
          font-weight: 655;
          border-radius: 0 0 3rem 0;

          @include mq($mq-sm) {
            font-size: 20px;
            padding: 18px 35px;
          }
        }
      }

      .card__image {
        --card-image-padding: 2rem 0 0 0;
        @include mq($mq-sm) {
          height: 100%;
          display: flex;
          align-items: center;
          --card-image-padding: 3rem;
        }
        @include mq($mq-md) {
          --card-image-padding: 0 1.5rem 0 0;
        }

        .card__image-container {
          max-height: 400px;
        }
      }

      .card__label {
        display: flex;
        flex-direction: column;
        font-size: 16px;
        margin-bottom: 8px;
      }

      .card__heading {
        flex: 1;
        flex-direction: column;
        font-size: 24px;
        font-weight: 800;
        line-height: 1.2;

        @include mq($mq-md) {
          font-size: 32px;
        }
      }

      .card__content {
        flex: 0;
      }

      .price__amount--highlight {
        color: currentColor;
      }

      .square-badge {
        padding: 20px 26px;
        @include mq($mq-sm) {
          padding: 1.2rem 2rem;
        }
        margin: 4px;
        --square-badge-radius: 0 calc(3rem - 4px) 0;
      }

      &.bg-primary {
        .card__background::before {
          background-color: var(--color-bg-inverse);
        }

        .square-badge {
          --square-badge-bg: var(--color-bg-inverse);
        }

        &::after {
          display: none;
        }
      }
    }

    &.card--promo {
      @include mq($mq-md) {
        height: 450px;
      }

      .card__image {
        --card-image-padding: 0;
      }

      .card__label {
        flex: 1;
        display: flex;
        justify-content: end;
        flex-direction: column;
        font-size: 16px;
        margin-bottom: 8px;

        img {
          filter: invert(100%);
        }
      }

      .card__heading {
        flex: 1;
        margin-bottom: 24px;
        .heading {
          font-size: 24px;
          font-weight: 800;
          line-height: 1.2;

          @include mq($mq-md) {
            font-size: 32px;
          }
        }
      }

      .card__content {
        flex: 0;
      }
    }

    &.bg-primary {
      --card-bg-color: var(--color-primary);

      .offer-tile__go-to-offer.link-button {
        background-color: var(--color-bg-inverse);
      }
    }
  }

  :deep(.square-badge) {
    --square-badge-bg: var(--color-primary);
  }
}
</style>
